import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Check, Terminal, AlertCircle } from 'lucide-react';
import { API_URL } from '../lib/api';
import { AuthLayout } from '../components/AuthLayout';
import { buildLoginRedirectPath } from '../lib/login-redirect';

export function DeviceAuthPage() {
  const { isLoading: authLoading, isAuthenticated } = useAuth();
  const searchParams = new URLSearchParams(window.location.search);
  const codeFromUrl = searchParams.get('code') || '';

  const [userCode, setUserCode] = useState(codeFromUrl);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      const returnPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      // Full reload ensures the OAuth flow starts from a clean session state.
      window.location.assign(buildLoginRedirectPath(returnPath));
    }
  }, [authLoading, isAuthenticated]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!userCode.trim() || submitting) return;

    setSubmitting(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/api/v2/cloud/device-approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userCode: userCode.trim() }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to approve device');
      }

      setSuccess(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to approve device',
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (authLoading) {
    return (
      <AuthLayout>
        <div className="text-center text-white/60">Loading...</div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout maxWidth="sm">
      {success ? (
        <div className="rounded-xl border border-green-500/20 bg-green-500/10 p-6 text-center">
          <Check className="h-12 w-12 text-green-400 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-white mb-2">
            Device Authorized
          </h2>
          <p className="text-white/60 text-sm">
            You can return to your terminal. The CLI will receive an API key
            automatically.
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-center gap-3 mb-2">
            <Terminal className="h-5 w-5 text-white/60" />
            <h1 className="text-lg font-semibold text-white">
              Authorize Device
            </h1>
          </div>

          <p className="text-white/40 text-sm mb-6 text-center">
            Check that this code matches the code in your agent window, then
            authorize this device.
          </p>

          <form onSubmit={handleSubmit}>
            <input
              type="text"
              value={userCode}
              onChange={(e) => setUserCode(e.target.value.toUpperCase())}
              placeholder="XXXX-XXXX"
              maxLength={9}
              className="w-full rounded-xl bg-white/[0.07] border border-white/[0.08] px-4 py-3 text-center text-2xl font-mono tracking-[0.3em] text-white placeholder:text-white/20 focus:outline-none focus:ring-2 focus:ring-white/20"
              autoFocus
            />

            {error && (
              <div className="mt-4 flex items-start gap-2 rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-300">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={!userCode.trim() || submitting}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-white/[0.07] border border-white/[0.08] px-4 py-3 text-sm font-semibold text-white transition-all duration-150 hover:bg-white/[0.12] hover:border-white/[0.15] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Authorizing...' : 'Authorize'}
            </button>
          </form>
        </>
      )}
    </AuthLayout>
  );
}
